const SUPPORTED_MESH_TYPES = ['stl', 'ply', 'obj', 'vmd'];

export const FINDING_MEDIA_COLORS = Object.freeze({
  amalgam: '#8ca63f',
  caries: '#78bff8',
  tartar: '#ff812f',
  brokenTooth: '#e4c500',
  oldFilling: '#965500',
  missingTooth: '#fa68ff',
  gingivitis: '#5de2c5',
  discoloration: '#7f54ff',
  unknown: '#808080',
});

const FINDING_CLASS_KEY_BY_ID = Object.freeze({
  0: 'amalgam',
  1: 'caries',
  2: 'tartar',
  4: 'brokenTooth',
  5: 'oldFilling',
  6: 'missingTooth',
  7: 'gingivitis',
  8: 'discoloration',
});

const FINDING_CLASS_KEY_BY_NAME = Object.freeze({
  amalgam: 'amalgam',
  carie: 'caries',
  caries: 'caries',
  tartar: 'tartar',
  tartre: 'tartar',
  letartre: 'tartar',
  brokentooth: 'brokenTooth',
  dentcassee: 'brokenTooth',
  oldfilling: 'oldFilling',
  ancienneobturation: 'oldFilling',
  missingtooth: 'missingTooth',
  dentmanquante: 'missingTooth',
  gingivitis: 'gingivitis',
  gingivite: 'gingivitis',
  discoloration: 'discoloration',
  decoloration: 'discoloration',
});

const normalizeFindingName = (findingName = '') =>
  findingName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export const getFindingClassKey = ({ classId, findingName } = {}) =>
  FINDING_CLASS_KEY_BY_ID[Number(classId)] ||
  FINDING_CLASS_KEY_BY_NAME[normalizeFindingName(findingName)] ||
  'unknown';

export const getFindingMediaColor = (finding) =>
  FINDING_MEDIA_COLORS[getFindingClassKey(finding)] || FINDING_MEDIA_COLORS.unknown;

export const getMeshFileType = (filename = '') => {
  const lowerName = filename.toLowerCase();
  return SUPPORTED_MESH_TYPES.find((type) => lowerName.endsWith(`.${type}`)) || null;
};

export const isFindingsFile = (filename = '') => {
  const lowerName = filename.toLowerCase();
  return lowerName.startsWith('pbox_save') && lowerName.endsWith('.json');
};

const isValidPosition = (position) =>
  Array.isArray(position) &&
  position.length === 3 &&
  position.every((value) => Number.isFinite(Number(value)));

const getMarkerPosition = (marker) => {
  if (isValidPosition(marker.PosView3d_get)) {
    return {
      position: marker.PosView3d_get.map(Number),
      positionSpace: 'view',
    };
  }

  // Older pbox files serialized the current 3D-view position as `pos`.
  if (isValidPosition(marker.pos)) {
    return { position: marker.pos.map(Number), positionSpace: 'view' };
  }

  // Legacy fallback: posMesh is local to its jaw and still needs transform.json.
  if (isValidPosition(marker.posMesh)) {
    return { position: marker.posMesh.map(Number), positionSpace: 'mesh' };
  }

  return null;
};

export const normalizeFindings = (payload) => {
  if (!payload || !Array.isArray(payload.photoData)) return [];

  return payload.photoData
    .filter((photo) => photo && photo.type === 'ai' && photo.marker)
    .map((photo) => {
      const marker = photo.marker;
      const markerPosition = getMarkerPosition(marker);
      if (!markerPosition) return null;

      const cards = Array.isArray(marker.cards) ? marker.cards : [];
      const primaryCard = cards[0] || {};

      return {
        id: marker.num,
        source: photo.type,
        ...markerPosition,
        ownerMeshType: Number(marker.ownerMeshType),
        toothNumber: marker.toothNumber,
        classId: primaryCard.classId,
        originalClassId: primaryCard.originalClassId,
        findingName: primaryCard.findingName || photo.comment || 'Finding',
        plan: primaryCard.plan || null,
        comment: primaryCard.comment || photo.comment || null,
        status: primaryCard.status,
        acceptance: photo.acceptance || null,
      };
    })
    .filter(Boolean);
};

export const decodeFindings = (buffer) => {
  const json = new TextDecoder('utf-8').decode(buffer).replace(/^\uFEFF/, '');
  return normalizeFindings(JSON.parse(json));
};
